import React, { useEffect, useMemo, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container } from 'react-bootstrap';

// Layout components
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';

// Redux actions
import { checkAuth } from './store/authSlice';

// Hooks
import { useGlobalWebSocket } from './hooks/useGlobalWebSocket';

// Contexts
import { WebSocketProvider } from './contexts/WebSocketContext';

// Services
import monitoringIngestionScheduler from './services/MonitoringIngestionScheduler';

// Pages
import LoginPage from './pages/LoginPage';
import MonitoringPage from './pages/MonitoringPage';
import RequestAccountPage from './pages/RequestAccountPage';

// Lazy load other pages for better performance
const ATRDetailsPage = lazy(() => import('./pages/ATRDetailsPage'));
const ATRPage = lazy(() => import('./pages/ATRPage'));
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'));
const ConnectionsDashboardPage = lazy(() => import('./pages/ConnectionsDashboardPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const IngestPage = lazy(() => import('./pages/IngestPage'));
const MappingsPage = lazy(() => import('./pages/MappingsPage'));
const PipelinesPage = lazy(() => import('./pages/PipelinesPage'));
const PlatformsPage = lazy(() => import('./pages/PlatformsPage'));
const RecordsPage = lazy(() => import('./pages/RecordsPage'));
const SchemasPage = lazy(() => import('./pages/SchemasPage'));
const SensorsPage = lazy(() => import('./pages/SensorsPage'));
const SourcesPage = lazy(() => import('./pages/SourcesPage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const VideoMonitoringPage = lazy(() => import('./pages/VideoMonitoringPage'));
const VideoSourcesPage = lazy(() => import('./pages/VideoSourcesPage'));
const VideoViewerPage = lazy(() => import('./pages/VideoViewerPage'));

// Note: ProtectedRoute component removed as it's not being used
// Authentication is handled by the main App component routing logic

const DEBUG_MONITORING = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true';

const PageFallback = () => (
  <div className="p-4">
    <div className="placeholder-glow">
      <div className="placeholder col-6 mb-3" style={{ height: '2rem' }}></div>
      <div className="placeholder col-12 mb-2"></div>
      <div className="placeholder col-10 mb-2"></div>
      <div className="placeholder col-8 mb-2"></div>
    </div>
  </div>
);

const LazyPage = ({ children }) => (
  <Suspense fallback={<PageFallback />}>
    {children}
  </Suspense>
);

// Admin-only route component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useSelector((state) => state.auth);

  if (DEBUG_MONITORING) {
    console.log('AdminRoute: Current state:', { isAuthenticated, loading, user: user?.role });
  }

  if (loading) {
    if (DEBUG_MONITORING) {
      console.log('AdminRoute: Still loading auth...');
    }
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    if (DEBUG_MONITORING) {
      console.log('AdminRoute: Not authenticated, redirecting to login');
    }
    return <Navigate to="/login" />;
  }

  // Wait for user data to be fully loaded before checking role
  if (!user) {
    if (DEBUG_MONITORING) {
      console.log('AdminRoute: User data not loaded yet...');
    }
    return <div>Loading user data...</div>;
  }

  // Check if user has admin role
  if (user.role !== 'ADMIN') {
    if (DEBUG_MONITORING) {
      console.log('AdminRoute: User role is not ADMIN:', user.role, 'redirecting to monitoring');
    }
    return <Navigate to="/monitoring" replace />;
  }

  if (DEBUG_MONITORING) {
    console.log('AdminRoute: Access granted for ADMIN user');
  }
  return children;
};

// Consumer-accessible route component (Data Monitoring, Records)
const ConsumerRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useSelector((state) => state.auth);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Wait for user data to be fully loaded before checking role
  if (!user) {
    return <div>Loading user data...</div>;
  }

  // Allow both ADMIN and CONSUMER roles
  if (user.role !== 'ADMIN' && user.role !== 'CONSUMER') {
    return <Navigate to="/monitoring" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading } = useSelector((state) => state.auth);

  // Initialize global WebSocket connection for all real-time features
  const token = user?.token || sessionStorage.getItem('token');
  const { connect, disconnect } = useGlobalWebSocket(isAuthenticated ? token : null);

  // Memoize WebSocket functions to prevent unnecessary re-renders
  const webSocketFunctions = useMemo(() => ({
    connect,
    disconnect
  }), [connect, disconnect]);

  useEffect(() => {
    if (DEBUG_MONITORING) {
      console.log('App: Starting authentication check...');
    }
    dispatch(checkAuth());

    // Note: We're using sessionStorage instead of localStorage for token storage.
    // This ensures that when the user closes the tab or browser, the token is
    // automatically cleared and they will be logged out when they return.

    // However, monitoring data is stored in localStorage, so we need to clear it
    // when the page is closed to prevent data persistence across sessions
    const handleBeforeUnload = () => {
      // Clear monitoring data from localStorage on page close
      const localKeys = Object.keys(localStorage);
      localKeys.forEach(key => {
        if (key.startsWith('leader-ui-') || key.includes('monitoring') || key.includes('filmstrip') || key.includes('atr') || key.includes('video')) {
          localStorage.removeItem(key);
        }
      });

      if (DEBUG_MONITORING) {
        console.log('Page close: Cleared monitoring data from localStorage');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch]);

  useEffect(() => {
    if (DEBUG_MONITORING) {
      console.log('App: Auth state changed:', { isAuthenticated, userRole: user?.role });
      console.log('App: Current URL:', window.location.pathname);
    }
  }, [isAuthenticated, user]);

  // Start/stop monitoring data processors based on authentication status
  useEffect(() => {
    if (isAuthenticated) {
      // Start the shared monitoring ingestion scheduler when user is authenticated
      monitoringIngestionScheduler.start();
    } else {
      // Stop monitoring ingestion when user is not authenticated
      monitoringIngestionScheduler.stop();
    }

    // Cleanup on unmount
    return () => {
      monitoringIngestionScheduler.stop();
    };
  }, [isAuthenticated]);

  // Show skeleton layout while authentication is being checked
  if (loading) {
    if (DEBUG_MONITORING) {
      console.log('App: Authentication loading, showing skeleton layout...');
    }
    return (
      <div className="App">
        <div className="d-flex">
          {/* Skeleton sidebar */}
          <div style={{ width: '250px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="p-3">
              <div className="placeholder-glow">
                <div className="placeholder col-8 mb-3"></div>
                <div className="placeholder col-6 mb-2"></div>
                <div className="placeholder col-7 mb-2"></div>
                <div className="placeholder col-5 mb-2"></div>
                <div className="placeholder col-6 mb-2"></div>
              </div>
            </div>
          </div>
          {/* Skeleton main content */}
          <div className="flex-grow-1">
            {/* Skeleton header */}
            <div style={{ height: '60px', backgroundColor: '#fff', borderBottom: '1px solid #dee2e6' }}>
              <div className="d-flex justify-content-between align-items-center h-100 px-3">
                <div className="placeholder-glow">
                  <div className="placeholder col-4"></div>
                </div>
                <div className="placeholder-glow">
                  <div className="placeholder col-3"></div>
                </div>
              </div>
            </div>
            {/* Skeleton content */}
            <div className="p-4">
              <div className="placeholder-glow">
                <div className="placeholder col-6 mb-3" style={{ height: '2rem' }}></div>
                <div className="placeholder col-12 mb-2"></div>
                <div className="placeholder col-10 mb-2"></div>
                <div className="placeholder col-8 mb-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {isAuthenticated ? (
        <Routes>
          {/* Documentation routes - full screen layout */}
          <Route path="/docs" element={
            <ConsumerRoute>
              <LazyPage>
                <DocumentationPage />
              </LazyPage>
            </ConsumerRoute>
          } />
          <Route path="/docs/*" element={
            <ConsumerRoute>
              <LazyPage>
                <DocumentationPage />
              </LazyPage>
            </ConsumerRoute>
          } />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/monitoring" replace />} />

          {/* All other routes - standard layout with sidebar */}
          <Route path="/users" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <UsersPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/sources" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <SourcesPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/video-sources" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <VideoSourcesPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/suppliers" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <SuppliersPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/platforms" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <PlatformsPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/sensors" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <SensorsPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/channels" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <ChannelsPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/pipelines" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <PipelinesPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/mappings" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <MappingsPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/schemas" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <SchemasPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/records" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <ConsumerRoute>
                    <LazyPage>
                      <RecordsPage />
                    </LazyPage>
                  </ConsumerRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/ingest" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <IngestPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/upload" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <UploadPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/monitoring" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <ConsumerRoute>
                    <WebSocketProvider {...webSocketFunctions}>
                      <MonitoringPage />
                    </WebSocketProvider>
                  </ConsumerRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/video-monitoring" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <ConsumerRoute>
                    <LazyPage>
                      <VideoMonitoringPage />
                    </LazyPage>
                  </ConsumerRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/atr" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <ConsumerRoute>
                    <LazyPage>
                      <ATRPage />
                    </LazyPage>
                  </ConsumerRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/atr-details/:sourceId" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <ConsumerRoute>
                    <LazyPage>
                      <ATRDetailsPage />
                    </LazyPage>
                  </ConsumerRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/video-viewer/:videoSourceId" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <ConsumerRoute>
                    <LazyPage>
                      <VideoViewerPage />
                    </LazyPage>
                  </ConsumerRoute>
                </Container>
              </div>
            </div>
          } />
          <Route path="/connections" element={
            <div className="d-flex">
              <Sidebar />
              <div className="flex-grow-1">
                <Header />
                <Container fluid className="main-content">
                  <AdminRoute>
                    <LazyPage>
                      <ConnectionsDashboardPage />
                    </LazyPage>
                  </AdminRoute>
                </Container>
              </div>
            </div>
          } />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/monitoring" replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/request-account" element={<RequestAccountPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </div>
  );
}

export default App;
